import { buildCategoryNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { IRouteMetadata, Route } from "@faire/web-api/Route";
import {
  props as c1SuppliersRouteProps,
  PathParameters as c1SuppliersPathParameters,
} from "@faire/web-api/routes/suppliers/c1SuppliersUrlId";
import {
  props as c1SuppliersBrandValueRouteProps,
  PathParameters as c1SuppliersBrandValuePathParameters,
} from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/brand-value/brandValue";
import {
  props as c2SuppliersRouteProps,
  PathParameters as c2SuppliersPathParameters,
} from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId";
import {
  props as c3SuppliersRouteProps,
  PathParameters as c3SuppliersPathParameters,
} from "@faire/web-api/routes/suppliers/c1SuppliersUrlId/subcategory/c2SuppliersUrlId/c3SuppliersUrlId";
import { logAssert } from "@faire/web/common/logging";
import { Route as UnknownRoute } from "@faire/web/ui/routing/Route";
import { Switch } from "@faire/web/ui/routing/Switch";
import React, { Suspense } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";

import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { fromUrlIds } from "@faire/retailer/models/Category/fromUrlIds";
import { useRetailerRoutingContext } from "@faire/retailer/RetailerRoutingContextProvider";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { getCategoryRedirect } from "@faire/retailer/stores/domain/CategoryStore/getCategoryRedirect";
import { CategoryRouter } from "@faire/retailer/views/category/CategoryRouter";
import { LazySuppliersPage } from "@faire/retailer/views/suppliers/LazySuppliersPage";

import { SuppliersPageLoadingSkeleton } from "./SuppliersPageLoadingSkeleton/SuppliersPageLoadingSkeleton";

const suppliersRoutes: Array<IRouteMetadata & { path: string }> = [
  c3SuppliersRouteProps,
  c2SuppliersRouteProps,
  c1SuppliersRouteProps,
  c1SuppliersBrandValueRouteProps,
];

export const SuppliersRouter: React.FC = () => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  return (
    <Switch>
      {isLoggedInRetailer ? (
        <UnknownRoute path="/suppliers/All Brands" component={CategoryRouter} />
      ) : null}
      <UnknownRoute path="/suppliers" component={SuppliersRouterInner} />
    </Switch>
  );
};

const SuppliersRouterInner = () => {
  // before rendering the component, we need to make sure the category tree is loaded
  const { categoriesLoading } = useCategoryStore();

  if (categoriesLoading) {
    return null;
  }

  return (
    <Suspense fallback={<SuppliersPageLoadingSkeleton />}>
      <Switch>
        {suppliersRoutes.map((route) => (
          <Route
            {...route}
            exact
            key={route.path}
            component={SuppliersPageRenderer}
          />
        ))}
      </Switch>
    </Suspense>
  );
};

type ISuppliersPageMatch = Partial<
  c1SuppliersPathParameters &
    c2SuppliersPathParameters &
    c3SuppliersPathParameters &
    c1SuppliersBrandValuePathParameters
>;

const SuppliersPageRenderer = ({
  match,
  location,
}: RouteComponentProps<ISuppliersPageMatch>) => {
  const categoryUrlIds = extractSuppliersUrlIds(match.params);
  const { categories } = useCategoryStore();
  const routingContext = useRetailerRoutingContext();
  const category = fromUrlIds(categoryUrlIds, categories ?? []);

  logAssert(
    !!category,
    `Category creation failed on url, Pathname: ${location.pathname}. Search: ${location.search}`
  );
  const categoryRedirectUrl = getCategoryRedirect(
    category,
    {
      location,
      match,
    },
    categories ?? [],
    routingContext
  );
  if (categoryRedirectUrl !== undefined) {
    return <Redirect to={categoryRedirectUrl} />;
  }

  const { mostSpecificPathIdentifier } = category;
  const c1SuppliersUrlId = categoryUrlIds[0];
  const key = `${mostSpecificPathIdentifier}${c1SuppliersUrlId}`;
  const navigationPath = buildCategoryNavigationPath(categoryUrlIds);
  return (
    <Suspense fallback={<SuppliersPageLoadingSkeleton />}>
      <LazySuppliersPage
        key={key}
        category={category}
        navigationPath={navigationPath}
      />
    </Suspense>
  );
};

const extractSuppliersUrlIds = ({
  c1SuppliersUrlId,
  c2SuppliersUrlId,
  c3SuppliersUrlId,
}: ISuppliersPageMatch): string[] => {
  return [
    c1SuppliersUrlId ?? "",
    c2SuppliersUrlId ?? "",
    c3SuppliersUrlId ?? "",
  ]
    .filter((c) => c !== "")
    .map(decodeURIComponent);
};
